import { useState, useEffect } from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { PropertyCategory } from '@/types';

interface PropertySearchProps {
  onSearch: (filters: PropertyFilters) => void;
  properties: Property[];
  settings?: {
    showSearch?: boolean;
    showPriceRange?: boolean;
    showFilters?: boolean;
    showStatus?: boolean;
    showCategory?: boolean;
    showCity?: boolean;
    showBedrooms?: boolean;
    showBathrooms?: boolean;
    showSuites?: boolean;
    showParkingSpaces?: boolean;
    showArea?: boolean;
  };
}

export interface PropertyFilters {
  query: string;
  status: string;
  minPrice: number;
  maxPrice: number;
  minArea: number;
  maxArea: number;
  bedrooms: string;
  bathrooms: string;
  suites: string;
  parkingSpaces: string;
  category: string;
  city: string;
}

const initialFilters: PropertyFilters = {
  query: '',
  status: 'todos',
  minPrice: 0,
  maxPrice: 10000000,
  minArea: 0,
  maxArea: 1000,
  bedrooms: 'todos',
  bathrooms: 'todos',
  suites: 'todos',
  parkingSpaces: 'todos',
  category: 'todos',
  city: 'todos'
};

const categories: PropertyCategory[] = [
  'Lançamento',
  'Na planta',
  'Em Construção',
  'Pronto para morar'
];

export default function PropertySearch({ onSearch, properties, settings = {} }: PropertySearchProps) {
  const [filters, setFilters] = useState<PropertyFilters>(initialFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [cities, setCities] = useState<string[]>([]);

  // Default settings if not provided
  const {
    showSearch = true,
    showPriceRange = true,
    showFilters = true,
    showStatus = true,
    showCategory = true,
    showCity = true,
    showBedrooms = true,
    showBathrooms = true,
    showSuites = true,
    showParkingSpaces = true,
    showArea = true
  } = settings;

  useEffect(() => {
    // Get unique cities from properties
    const uniqueCities = [...new Set(properties
      .map(property => property.city)
      .filter(Boolean)
      .sort()
    )];
    setCities(uniqueCities);
  }, [properties]);

  const handleSearch = () => {
    onSearch(filters);
    setIsOpen(false);
  };

  const handleReset = () => {
    setFilters(initialFilters);
    onSearch(initialFilters);
    setIsOpen(false);
  };

  // If filters are disabled, don't render the component
  if (!showFilters) {
    return null;
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-4">
      <div className="flex gap-2">
        {showSearch && (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar imóveis..."
              value={filters.query}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, query: e.target.value }));
                onSearch({ ...filters, query: e.target.value });
              }}
              className="pl-9"
            />
          </div>
        )}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filtros
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md">
            <SheetHeader>
              <SheetTitle>Filtros de Busca</SheetTitle>
              <SheetDescription>
                Refine sua busca usando os filtros abaixo
              </SheetDescription>
            </SheetHeader>
            
            <div className="py-6 space-y-6">
              {showStatus && (
                <Tabs 
                  value={filters.status} 
                  onValueChange={(value) => {
                    setFilters(prev => ({ ...prev, status: value }));
                    onSearch({ ...filters, status: value });
                  }}
                  className="w-full"
                >
                  <TabsList className="w-full">
                    <TabsTrigger value="todos" className="flex-1">Todos</TabsTrigger>
                    <TabsTrigger value="venda" className="flex-1">Venda</TabsTrigger>
                    <TabsTrigger value="aluguel" className="flex-1">Aluguel</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              {showCategory && (
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select
                    value={filters.category}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas as categorias</SelectItem>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showCity && (
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Select
                    value={filters.city}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, city: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a cidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas as cidades</SelectItem>
                      {cities.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showPriceRange && (
                <div className="space-y-2">
                  <Label>Faixa de Preço</Label>
                  <div className="pt-2">
                    <Slider
                      min={0}
                      max={10000000}
                      step={50000}
                      value={[filters.minPrice, filters.maxPrice]}
                      onValueChange={([min, max]) => 
                        setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }))
                      }
                    />
                    <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                      <span>R$ {filters.minPrice.toLocaleString()}</span>
                      <span>R$ {filters.maxPrice.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              {showArea && (
                <div className="space-y-2">
                  <Label>Área (m²)</Label>
                  <div className="pt-2">
                    <Slider
                      min={0}
                      max={1000}
                      step={10}
                      value={[filters.minArea, filters.maxArea]}
                      onValueChange={([min, max]) => 
                        setFilters(prev => ({ ...prev, minArea: min, maxArea: max }))
                      }
                    />
                    <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                      <span>{filters.minArea} m²</span>
                      <span>{filters.maxArea} m²</span>
                    </div>
                  </div>
                </div>
              )}

              {showBedrooms && (
                <div className="space-y-2">
                  <Label>Quartos</Label>
                  <Select
                    value={filters.bedrooms}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, bedrooms: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Número de quartos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="1">1 quarto</SelectItem>
                      <SelectItem value="2">2 quartos</SelectItem>
                      <SelectItem value="3">3 quartos</SelectItem>
                      <SelectItem value="4">4+ quartos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showBathrooms && (
                <div className="space-y-2">
                  <Label>Banheiros</Label>
                  <Select
                    value={filters.bathrooms}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, bathrooms: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Número de banheiros" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="1">1 banheiro</SelectItem>
                      <SelectItem value="2">2 banheiros</SelectItem>
                      <SelectItem value="3">3 banheiros</SelectItem>
                      <SelectItem value="4">4+ banheiros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showSuites && (
                <div className="space-y-2">
                  <Label>Suítes</Label>
                  <Select
                    value={filters.suites}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, suites: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Número de suítes" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas</SelectItem>
                      <SelectItem value="0">Sem suítes</SelectItem>
                      <SelectItem value="1">1 suíte</SelectItem>
                      <SelectItem value="2">2 suítes</SelectItem>
                      <SelectItem value="3">3 suítes</SelectItem>
                      <SelectItem value="4">4+ suítes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showParkingSpaces && (
                <div className="space-y-2">
                  <Label>Vagas de Garagem</Label>
                  <Select
                    value={filters.parkingSpaces}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, parkingSpaces: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Número de vagas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas</SelectItem>
                      <SelectItem value="1">1 vaga</SelectItem>
                      <SelectItem value="2">2 vagas</SelectItem>
                      <SelectItem value="3">3 vagas</SelectItem>
                      <SelectItem value="4">4+ vagas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <SheetFooter>
              <Button variant="outline" onClick={handleReset}>
                Limpar Filtros
              </Button>
              <Button onClick={handleSearch}>
                Aplicar Filtros
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}